import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectEmployeeInformation,
  setPreviousEmploymentFresher,
  addPreviousEmployment,
  removePreviousEmployment,
  updatePreviousEmploymentField,
  setPreviousEmploymentImage,
  removePreviousEmploymentImage,
} from "../../../Redux/employeeInformationSlice.js";
import styles from "./PreviousEmploymentDetails.module.scss";

const createEmploymentEntry = () => ({
  id: Date.now(),
  company_name: "",
  start_date: "",
  end_date: "",
  years_of_experience: "",
  month_of_experience: "",
  designation: "",
  certificate: "",
  is_fresher: false,
});

const calculateExperience = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return { years: "", months: "" };
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (
    Number.isNaN(start.getTime()) ||
    Number.isNaN(end.getTime()) ||
    end < start
  ) {
    return { years: "", months: "" };
  }

  let totalMonths =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth());

  if (end.getDate() < start.getDate()) {
    totalMonths -= 1;
  }

  totalMonths = Math.max(totalMonths, 0);

  return {
    years: String(Math.floor(totalMonths / 12)),
    months: String(totalMonths % 12),
  };
};

const PreviousEmploymentDetails = forwardRef(
  function PreviousEmploymentDetails(_, ref) {
    const dispatch = useDispatch();

    const { previousEmploymentDetails, previousEmploymentImages } = useSelector(
      selectEmployeeInformation,
    );

    const { isFresher, entries } = previousEmploymentDetails;

    const [errorFields, setErrorFields] = useState({});

    const clearError = (id, name) => {
      setErrorFields((prev) => {
        const entryErrors = new Set(prev[id] || []);
        entryErrors.delete(name);
        return {
          ...prev,
          [id]: entryErrors,
        };
      });
    };

    const hasError = (id, name) => errorFields[id]?.has(name) ?? false;

    const handleAddEmployment = () => {
      dispatch(addPreviousEmployment(createEmploymentEntry()));
    };

    const handleRemoveEmployment = (id) => {
      if (previousEmploymentImages[id]?.url) {
        URL.revokeObjectURL(previousEmploymentImages[id].url);
      }

      dispatch(removePreviousEmployment(id));

      setErrorFields((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    };

    const updateField = (id, name, value) => {
      dispatch(updatePreviousEmploymentField({ id, name, value }));
      clearError(id, name);
    };

    const handleFile = (id, file) => {
      if (!file || file.size > 10 * 1024 * 1024) {
        return;
      }

      if (previousEmploymentImages[id]?.url) {
        URL.revokeObjectURL(previousEmploymentImages[id].url);
      }

      dispatch(
        setPreviousEmploymentImage({
          id,
          image: {
            name: file.name,
            file,
            url: URL.createObjectURL(file),
          },
        }),
      );
    };

    const getMissingFields = useCallback(() => {
      if (isFresher) {
        return [];
      }

      if (entries.length === 0) {
        return ["Previous Employment"];
      }

      const missing = [];
      const newErrorFields = {};

      entries.forEach((entry, index) => {
        const label = `Employment ${index + 1}`;
        const entryErrors = new Set();

        if (!String(entry.company_name || "").trim()) {
          missing.push(`${label} Company Name`);
          entryErrors.add("company_name");
        }

        if (!entry.start_date) {
          missing.push(`${label} Start Date`);
          entryErrors.add("start_date");
        }

        if (!entry.end_date) {
          missing.push(`${label} End Date`);
          entryErrors.add("end_date");
        }

        if (!String(entry.designation || "").trim()) {
          missing.push(`${label} Designation`);
          entryErrors.add("designation");
        }

        if (entry.start_date && entry.end_date) {
          const start = new Date(entry.start_date);
          const end = new Date(entry.end_date);

          if (end < start) {
            missing.push(`${label} valid date range`);
            entryErrors.add("start_date");
            entryErrors.add("end_date");
          }
        }

        newErrorFields[entry.id] = entryErrors;
      });

      setErrorFields(newErrorFields);

      return missing;
    }, [entries, isFresher]);

    useImperativeHandle(ref, () => ({
      getMissingFields,
      isComplete: () => getMissingFields().length === 0,
    }));

    return (
      <div className={styles.previousEmploymentDetails}>
        <header className={styles.sectionHeader}>
          Previous Employee Details
        </header>

        <div className={styles.content}>
          <label className={styles.fresherCheck}>
            <input
              type="checkbox"
              checked={isFresher}
              onChange={(event) => {
                const checked = event.target.checked;

                dispatch(setPreviousEmploymentFresher(checked));

                if (!checked && entries.length === 0) {
                  dispatch(addPreviousEmployment(createEmploymentEntry()));
                }
              }}
            />
            <span>Employee is fresher ?</span>
          </label>

          {!isFresher ? (
            <button
              type="button"
              className={styles.addButton}
              onClick={handleAddEmployment}
              aria-label="Add previous employment"
              title="Add previous employment"
            >
              +
            </button>
          ) : null}

          {!isFresher && entries.length > 0 ? (
            <div className={styles.entries}>
              {entries.map((entry, index) => {
                const experience = calculateExperience(
                  entry.start_date,
                  entry.end_date,
                );

                return (
                  <section className={styles.entry} key={entry.id}>
                    <div className={styles.entryHeader}>
                      <h3>Employment {index + 1}</h3>

                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={() => handleRemoveEmployment(entry.id)}
                        aria-label={`Remove employment ${index + 1}`}
                        title="Remove employment"
                      >
                        ×
                      </button>
                    </div>

                    <div className={styles.formGrid}>
                      <label className={styles.field}>
                        <span>
                          Company Name<b>*</b>
                        </span>
                        <input
                          value={entry.company_name || ""}
                          className={
                            hasError(entry.id, "company_name")
                              ? styles.inputError
                              : ""
                          }
                          onChange={(event) =>
                            updateField(
                              entry.id,
                              "company_name",
                              event.target.value.toUpperCase(),
                            )
                          }
                        />
                        {hasError(entry.id, "company_name") && (
                          <p className={styles.fieldError}>
                            Please fill this field
                          </p>
                        )}
                      </label>

                      <label className={styles.field}>
                        <span>
                          Start Date<b>*</b>
                        </span>
                        <input
                          type="date"
                          value={entry.start_date || ""}
                          className={
                            hasError(entry.id, "start_date")
                              ? styles.inputError
                              : ""
                          }
                          onChange={(event) =>
                            updateField(
                              entry.id,
                              "start_date",
                              event.target.value,
                            )
                          }
                        />
                        {hasError(entry.id, "start_date") && (
                          <p className={styles.fieldError}>
                            Please select a valid start date
                          </p>
                        )}
                      </label>

                      <label className={styles.field}>
                        <span>
                          End Date<b>*</b>
                        </span>
                        <input
                          type="date"
                          value={entry.end_date || ""}
                          className={
                            hasError(entry.id, "end_date")
                              ? styles.inputError
                              : ""
                          }
                          onChange={(event) =>
                            updateField(
                              entry.id,
                              "end_date",
                              event.target.value,
                            )
                          }
                        />
                        {hasError(entry.id, "end_date") && (
                          <p className={styles.fieldError}>
                            Please select a valid end date
                          </p>
                        )}
                      </label>

                      <label className={styles.field}>
                        <span>
                          Year Of Experience<b>*</b>
                        </span>
                        <input
                          readOnly
                          value={entry.years_of_experience || experience.years}
                        />
                      </label>

                      <label className={styles.field}>
                        <span>
                          Designation<b>*</b>
                        </span>
                        <input
                          value={entry.designation || ""}
                          className={
                            hasError(entry.id, "designation")
                              ? styles.inputError
                              : ""
                          }
                          onChange={(event) =>
                            updateField(
                              entry.id,
                              "designation",
                              event.target.value.toUpperCase(),
                            )
                          }
                        />
                        {hasError(entry.id, "designation") && (
                          <p className={styles.fieldError}>
                            Please fill this field
                          </p>
                        )}
                      </label>

                      <label className={styles.field}>
                        <span>Month Of Experience</span>
                        <input
                          readOnly
                          value={entry.month_of_experience || experience.months}
                        />
                      </label>
                    </div>

                    <div className={styles.uploadBlock}>
                      <span>File Upload</span>

                      <p className={styles.note}>
                        Note: Upload the picture in JPG, JPEG, or PNG format.
                        Maximum size: 10 MB.
                      </p>

                      {previousEmploymentImages[entry.id] ? (
                        <div className={styles.previewRow}>
                          <span>{previousEmploymentImages[entry.id].name}</span>
                          <button
                            type="button"
                            className={styles.fileRemoveButton}
                            onClick={() =>
                              dispatch(removePreviousEmploymentImage(entry.id))
                            }
                          >
                            Remove
                          </button>
                        </div>
                      ) : entry.certificate ? (
                        <div className={styles.previewRow}>
                          <a
                            href={entry.certificate}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View Uploaded File
                          </a>
                        </div>
                      ) : (
                        <label className={styles.uploadButton}>
                          Upload Image
                          <input
                            type="file"
                            className={styles.hiddenInput}
                            accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                            onChange={(event) =>
                              handleFile(entry.id, event.target.files?.[0])
                            }
                          />
                        </label>
                      )}
                    </div>
                  </section>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);

export default PreviousEmploymentDetails;
